// Feasibility Analysis Types and Enums for Israeli Urban Renewal
// Based on TAMA 38 and Pinuy-Binuy programs

/**
 * Urban renewal program types in Israel
 * @typedef {'TAMA38_1' | 'TAMA38_2' | 'PINUY_BINUY'} Program
 */

/**
 * Three-state boolean for uncertain data
 * @typedef {'Yes' | 'No' | 'Unknown'} YesNoUnknown
 */

/**
 * @typedef {Object} FeasibilityInput
 * @property {string} address - Full address string
 * @property {string} municipalityName - Municipality name in Hebrew
 * @property {string} [municipalityCode] - Municipal code
 * @property {string} [gush] - גוש number
 * @property {string} [helka] - חלקה number
 * @property {number} [lotAreaSqm] - Lot area in square meters
 * @property {number|null} existingUnits - Number of existing housing units (null when insufficient data)
 * @property {number|null} avgExistingUnitAreaSqm - Average existing unit area (null when insufficient data)
 * @property {YesNoUnknown} pre1980Eligibility - Building built before 1980
 * @property {YesNoUnknown} tama38ValidInMunicipality - TAMA 38 valid in municipality
 * @property {YesNoUnknown} declaredPinuyBinuyArea - Declared Pinuy-Binuy area
 * @property {boolean} [conservationListed] - Heritage/conservation listed
 * @property {number} estimatedNewGFAsqm - Estimated new gross floor area
 * @property {number} saleableRatio - Ratio of saleable area (0.6-0.95)
 * @property {number} expectedSalePricePerSqmNIS - Expected sale price per sqm
 * @property {number} constructionCostPerSqmNIS - Construction cost per sqm
 * @property {number} parkingCostPerNewUnitNIS - Parking cost per new unit
 * @property {number} relocationCostPerExistingUnitNIS - Relocation cost per existing unit
 * @property {number} feesLeviesPermitsTotalNIS - Total fees, levies, permits
 * @property {number} ownerBonusAreaPerUnitSqm - Owner bonus area per unit
 * @property {number} [targetDeveloperProfitPct] - Target developer profit percentage
 * @property {number} [unitNetGfaHeuristicSqm] - Unit net GFA heuristic (default 95)
 */

/**
 * @typedef {Object} FeasibilityKPIs
 * @property {number} totalExistingAreaSqm - Total existing area
 * @property {number} ownerReturnAreaSqm - Area returned to owners
 * @property {number} estimatedNewUnits - Estimated number of new units
 * @property {number} saleableNewAreaSqm - Saleable new area
 * @property {number} saleableAreaForSaleSqm - Saleable area for sale
 * @property {number} revenueNIS - Total revenue
 * @property {number} constructionCostsNIS - Construction costs
 * @property {number} parkingCostsNIS - Parking costs
 * @property {number} relocationCostsNIS - Relocation costs
 * @property {number} feesLeviesNIS - Fees and levies
 * @property {number} totalCostsNIS - Total costs
 * @property {number} developerProfitPct - Developer profit percentage
 */

/**
 * @typedef {Object} Subscores
 * @property {number} economic - Economic subscore (0-100)
 * @property {number} planning - Planning subscore (0-100)
 * @property {number} consensus - Consensus subscore (0-100)
 * @property {number} execution - Execution subscore (0-100)
 */

/**
 * @typedef {Object} FeasibilityOutput
 * @property {Program} programSuggested - Suggested urban renewal program
 * @property {number} score0to100 - Overall feasibility score (0-100)
 * @property {Subscores} subscores - Individual subscores
 * @property {FeasibilityKPIs} kpis - Key performance indicators
 * @property {string[]} notes - Explanatory notes in Hebrew
 */

// Adapter interface types for government data sources

/**
 * @typedef {Object} GovMapParcel
 * @property {number} x - ITM X coordinate
 * @property {number} y - ITM Y coordinate
 * @property {string} municipalityCode - Municipality code
 * @property {string} municipalityName - Municipality name
 * @property {string} gush - גוש number
 * @property {string} helka - חלקה number
 * @property {number} [lotAreaSqm] - Lot area if known
 */

/**
 * @typedef {Object} PlanningRights
 * @property {number} [far] - Floor Area Ratio (יחס שטח רצפה)
 * @property {number} [maxFloors] - Maximum floors allowed
 * @property {number} [maxHeightM] - Maximum height in meters
 * @property {number} [rightsGFAsqm] - Rights GFA if stated in plan
 * @property {string} [parkingStd] - Parking standard (e.g., "1.1/unit")
 * @property {boolean} [conservationListed] - Conservation listed
 */

/**
 * @typedef {Object} PBStatus
 * @property {YesNoUnknown} declaredComplex - Declared Pinuy-Binuy complex
 * @property {string} [complexId] - Complex ID if declared
 */

/**
 * @typedef {Object} MunicipalityPolicy
 * @property {string} [tama38ValidUntil] - TAMA 38 valid until date (ISO)
 * @property {number} [bettermentLevyRatePB] - Betterment levy rate for PB
 */

/**
 * @typedef {Object} CompPoint
 * @property {number} priceNIS - Sale price in NIS
 * @property {number} areaSqm - Unit area in sqm
 * @property {string} transDate - Transaction date
 * @property {boolean} [newBuild] - Is new construction
 * @property {number} [distanceM] - Distance from subject property
 */

export const Programs = {
  TAMA38_1: 'TAMA38_1',
  TAMA38_2: 'TAMA38_2',
  PINUY_BINUY: 'PINUY_BINUY'
};

export const YesNoUnknownValues = {
  YES: 'Yes',
  NO: 'No',
  UNKNOWN: 'Unknown'
};


